import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebaseConfig';

/**
 * Component that protects routes requiring premium subscription
 * Redirects non-premium users to the welcome page for upgrade
 * Redirects unauthenticated users to the login page
 * NON-BLOCKING: Renders children immediately while checking status in background
 */
export default function PremiumProtectedRoute({ children }) {
  const [user, setUser] = useState(null);
  const [isPremium, setIsPremium] = useState(null); // null = unknown, true = premium, false = not premium
  const [checksComplete, setChecksComplete] = useState(false);
  
  useEffect(() => {
    let previousUid = null;
    
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        previousUid = currentUser.uid;
        
        // Check for cached premium status first
        const cacheKey = `premium_status_${currentUser.uid}`;
        const cachedData = sessionStorage.getItem(cacheKey);
        
        if (cachedData) {
          try {
            const { eligible, timestamp } = JSON.parse(cachedData);
            // Cache is valid for 5 minutes
            if (Date.now() - timestamp < 5 * 60 * 1000) {
              setIsPremium(eligible);
              setChecksComplete(true);
              return;
            }
          } catch (error) {
            console.error('Error parsing cached premium status:', error);
          }
        }
        
        // Check premium status via API in background
        try {
          const response = await fetch('/api/checkPremiumEligibility', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uid: currentUser.uid }),
          });
          
          if (response.ok) {
            const { eligible } = await response.json();
            setIsPremium(eligible);
            
            // Cache the result
            sessionStorage.setItem(cacheKey, JSON.stringify({
              eligible,
              timestamp: Date.now()
            }));
          } else {
            setIsPremium(false);
          }
        } catch (error) {
          console.error('Error checking premium status:', error);
          setIsPremium(false);
        }
        setChecksComplete(true);
      } else {
        // Not logged in - clear any cached data for previous user
        if (previousUid) {
          sessionStorage.removeItem(`premium_status_${previousUid}`);
        }
        setUser(null);
        setIsPremium(null);
        setChecksComplete(true);
      }
    });
    
    return () => unsubscribe();
  }, []);

  // Not logged in and checks complete - redirect to sign in
  if (checksComplete && !user) {
    return <Navigate to="/" replace />;
  }
  
  // Logged in but not premium (redirect immediately when status is determined)
  if (user && isPremium === false) {
    return <Navigate to="/welcome" replace />;
  }
  
  // Render children immediately (non-blocking) - will show content while checking or if premium
  return children;
} 